import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import {
  ArrowLeft, ArrowRight, BadgeCheck, BookOpen, Check, Lock, MapPin, MessageCircle, ShieldCheck, Upload,
} from "lucide-react";
import { DemoTag, EASE, MARK, gbp } from "@/components/common";
import { LOGISTICS_OPTIONS, MILE_RATE, PACKAGES, VEHICLES, WHATSAPP_DISPLAY, waLink } from "@/data/vehicles";
import { apiFetch } from "@/api";
import AgreementModal from "@/components/AgreementModal";

const STEPS = [
  "Select Vehicle", "Select Hire Package", "Select Dates", "Collection / Delivery",
  "Customer Details", "Driver Verification", "Rental Agreement", "Payment Summary", "Submit for Approval",
];

const fmtDate = (d) =>
  d ? new Date(d + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" }) : "—";

const Field = ({ label, testid, ...props }) => (
  <label className="block">
    <span className="field-label">{label}</span>
    <input className="field" data-testid={testid} {...props} />
  </label>
);

const FilePick = ({ label, hint, file, onPick, testid }) => (
  <div className="file-drop" data-testid={testid}>
    <input type="file" id={`${testid}-input`} className="hidden" accept=".jpg,.jpeg,.png,.webp,.pdf" onChange={(e) => onPick(e.target.files?.[0] || null)} />
    <label htmlFor={`${testid}-input`} className="flex items-center justify-between gap-4 cursor-pointer px-5 py-4">
      <span className={`text-sm font-light flex items-center gap-3 ${file ? "text-white" : "text-zinc-400"}`}>
        {file ? <Check size={14} className="text-emerald-300" /> : <Upload size={14} className="text-zinc-500" />}
        {file ? file.name : label}
      </span>
      <span className="text-[0.55rem] tracking-[0.25em] uppercase text-zinc-500">{file ? "Selected" : "Choose File"}</span>
    </label>
    {hint && <p className="px-5 pb-4 text-[0.62rem] tracking-[0.12em] text-zinc-600 font-light">{hint}</p>}
  </div>
);

const StepTitle = ({ n, title, sub }) => (
  <div className="mb-8">
    <span className="font-display text-zinc-600">{n}</span>
    <h2 className="font-display text-2xl sm:text-3xl text-white mt-2">{title}</h2>
    <p className="mt-3 text-sm text-zinc-400 font-light leading-relaxed max-w-xl">{sub}</p>
  </div>
);

const SummaryRow = ({ label, value }) => (
  <div className="flex items-center justify-between gap-6 py-3 border-b border-white/[0.06]">
    <span className="text-sm text-zinc-400 font-light">{label}</span>
    <span className="text-sm text-zinc-100 font-light text-right">{value}</span>
  </div>
);

const VehiclePhoto = ({ vehicle, compact = false }) => {
  const [idx, setIdx] = useState(0);
  if (!vehicle?.photos?.length) return null;
  return (
    <div data-testid="vehicle-photo-preview" className="veh-preview">
      <img src={vehicle.photos[idx]} alt={vehicle.name} className={compact ? "max-h-44 object-cover" : ""} />
      {vehicle.photos.length > 1 && (
        <div className="flex gap-2 p-3">
          {vehicle.photos.map((p, i) => (
            <button key={p} data-testid={`vehicle-photo-thumb-${i}`} onClick={() => setIdx(i)} className={`veh-thumb-btn ${i === idx ? "active" : ""}`}>
              <img src={p} alt="" className="w-16 h-11 object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const STRIPE_APPEARANCE = {
  theme: "night",
  variables: {
    colorPrimary: "#ffffff",
    colorBackground: "#0a0a0c",
    colorText: "#f5f5f4",
    colorDanger: "#fca5a5",
    fontFamily: "Jost, sans-serif",
    borderRadius: "0px",
  },
};

export default function Booking() {
  const [params] = useSearchParams();
  const [payConfig, setPayConfig] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const elementsOptions = useMemo(() => ({ mode: "payment", amount: 100, currency: "gbp", captureMethod: "manual", appearance: STRIPE_APPEARANCE }), []);

  useEffect(() => {
    apiFetch("/payments/config")
      .then((cfg) => {
        setPayConfig(cfg);
        if (cfg.enabled && cfg.publishable_key) setStripePromise(loadStripe(cfg.publishable_key));
      })
      .catch(() => setPayConfig({ enabled: false }));
  }, []);

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <BookingInner key={params.get("vehicle") || "none"} payConfig={payConfig} initialVehicle={params.get("vehicle") || ""} />
    </Elements>
  );
}

function BookingInner({ payConfig, initialVehicle }) {
  const stripe = useStripe();
  const elements = useElements();
  const [step, setStep] = useState(0);
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  const [payMethod, setPayMethod] = useState("card");
  const [payMounted, setPayMounted] = useState(false);
  const [agreementOpen, setAgreementOpen] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [distConfigured, setDistConfigured] = useState(null);
  const [liveDistance, setLiveDistance] = useState({ delivery: null, collection: null });
  const [distBusy, setDistBusy] = useState("");
  const [enquiry, setEnquiry] = useState({ name: "", phone: "", email: "", preferred_dates: "", message: "" });
  const [form, setForm] = useState({
    vehicleId: initialVehicle,
    packageId: "",
    startDate: "", endDate: "",
    logistics: "peterborough", deliveryMiles: "", collectionMiles: "",
    deliveryPostcode: "", collectionPostcode: "",
    name: "", dob: "", address: "", postcode: "", phone: "", email: "",
    licenceFront: null, licenceBack: null, proofDoc: null, niNumber: "",
    agreementAccepted: false,
  });

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setNotice(""); };
  const setEnq = (k, v) => { setEnquiry((f) => ({ ...f, [k]: v })); setNotice(""); };

  useEffect(() => { window.scrollTo(0, 0); }, [step, submitted]);

  const vehicle = useMemo(() => VEHICLES.find((v) => v.id === form.vehicleId), [form.vehicleId]);
  const pkg = useMemo(() => PACKAGES.find((p) => p.id === form.packageId), [form.packageId]);
  const isWeekend = form.packageId === "weekend";
  const enquiryOnly = !!vehicle?.enquiryOnly;

  const wantsDelivery = form.logistics === "delivery" || form.logistics === "both";
  const wantsCollection = form.logistics === "collection" || form.logistics === "both";

  const milesFor = (dir) => liveDistance[dir]?.miles ?? (Number(form[dir === "delivery" ? "deliveryMiles" : "collectionMiles"]) || 0);
  const chargeFor = (dir) => liveDistance[dir]?.charge ?? (milesFor(dir) > 0 ? Math.round(milesFor(dir) * MILE_RATE * 100) / 100 : 0);

  const hirePrice = vehicle && pkg && !enquiryOnly ? vehicle.prices[pkg.id] : 0;
  const deliveryCharge = wantsDelivery ? chargeFor("delivery") : 0;
  const collectionCharge = wantsCollection ? chargeFor("collection") : 0;
  const deposit = vehicle && !enquiryOnly ? vehicle.deposit : 0;
  const total = hirePrice + deliveryCharge + collectionCharge + (deposit || 0);

  useEffect(() => {
    if (elements && total > 0) elements.update({ amount: Math.round(total * 100) });
  }, [elements, total]);

  const nights = form.startDate && form.endDate
    ? Math.round((new Date(form.endDate) - new Date(form.startDate)) / 86400000)
    : 0;

  useEffect(() => {
    if (step === 3 && distConfigured === null) {
      apiFetch("/distance/quote?postcode=PE4 6BX")
        .then((r) => setDistConfigured(!!r.configured))
        .catch(() => setDistConfigured(false));
    }
  }, [step, distConfigured]);

  useEffect(() => {
    if (!distConfigured) return;
    const t = setTimeout(() => {
      if (wantsDelivery && form.deliveryPostcode.trim().length >= 5) calcDistance("delivery");
      if (wantsCollection && form.collectionPostcode.trim().length >= 5) calcDistance("collection");
    }, 800);
    return () => clearTimeout(t);
  }, [form.deliveryPostcode, form.collectionPostcode, distConfigured, wantsDelivery, wantsCollection]); // eslint-disable-line

  const onStartDate = (v) => {
    set("startDate", v);
    if (isWeekend) {
      if (!v) { set("endDate", ""); return; }
      const d = new Date(v + "T00:00:00");
      if (d.getDay() !== 5) {
        set("endDate", "");
        setNotice("Weekend hire starts on a Friday — please choose a Friday start date.");
        return;
      }
      const monday = new Date(d);
      monday.setDate(d.getDate() + 3);
      set("endDate", monday.toISOString().slice(0, 10));
    }
  };

  const calcDistance = async (dir) => {
    const postcode = dir === "delivery" ? form.deliveryPostcode : form.collectionPostcode;
    if (!postcode.trim()) { setNotice("Enter the postcode first."); return; }
    setDistBusy(dir);
    try {
      const r = await apiFetch(`/distance/quote?postcode=${encodeURIComponent(postcode.trim())}`);
      if (r.configured) {
        setLiveDistance((s) => ({ ...s, [dir]: { miles: r.miles, charge: r.charge } }));
      } else {
        setDistConfigured(false);
      }
    } catch (e) {
      setNotice(e.message);
    } finally {
      setDistBusy("");
    }
  };

  const canContinue = () => {
    switch (step) {
      case 0: return form.vehicleId ? "" : "Please select a vehicle to continue.";
      case 1: return enquiryOnly ? "" : form.packageId ? "" : "Please select a hire package.";
      case 2:
        if (!form.startDate || !form.endDate) return "Please select your hire dates.";
        if (isWeekend) {
          const d = new Date(form.startDate + "T00:00:00");
          if (d.getDay() !== 5) return "Weekend hire starts on a Friday.";
          const monday = new Date(d); monday.setDate(d.getDate() + 3);
          if (form.endDate !== monday.toISOString().slice(0, 10)) return "Weekend hire returns the following Monday morning.";
        }
        return "";
      case 3:
        if (wantsDelivery && !form.deliveryPostcode.trim()) return "Please enter your delivery postcode or location.";
        if (wantsCollection && !form.collectionPostcode.trim()) return "Please enter your collection postcode or location.";
        if (distConfigured && wantsDelivery && !liveDistance.delivery) return "Please calculate the delivery distance.";
        if (distConfigured && wantsCollection && !liveDistance.collection) return "Please calculate the collection distance.";
        return "";
      case 4: return form.name && form.dob && form.address && form.postcode && form.phone && form.email ? "" : "Please complete all customer details.";
      case 5: return form.licenceFront && form.licenceBack && form.proofDoc ? "" : "Please select the three required documents.";
      case 6: return form.agreementAccepted ? "" : "Please confirm you have read and agree to the agreement.";
      default: return "";
    }
  };

  const next = async () => {
    const err = canContinue();
    if (err) { setNotice(err); return; }
    if (step === 2 && vehicle && !enquiryOnly) {
      setBusy(true);
      try {
        const r = await apiFetch(`/availability?vehicle_id=${vehicle.id}&start=${form.startDate}&end=${form.endDate}`);
        if (!r.available) {
          setNotice("This vehicle is unavailable for the selected dates — please choose different dates or another vehicle.");
          setBusy(false);
          return;
        }
      } catch {
        setNotice("Could not check availability — please try again.");
        setBusy(false);
        return;
      }
      setBusy(false);
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const submitEnquiry = async () => {
    if (!enquiry.name.trim() || !enquiry.phone.trim()) { setNotice("Please add your name and phone number."); return; }
    setBusy(true);
    try {
      await apiFetch("/enquiries", { method: "POST", body: { vehicle_id: vehicle.id, ...enquiry } });
      setSubmitted({ enquiry: true, vehicleName: vehicle.name });
    } catch (e) {
      setNotice(e.message);
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    setNotice("");
    try {
      const res = await apiFetch("/bookings", {
        method: "POST",
        body: {
          vehicle_id: form.vehicleId,
          package_id: form.packageId,
          start_date: form.startDate,
          end_date: form.endDate,
          logistics: form.logistics,
          delivery_postcode: form.deliveryPostcode,
          collection_postcode: form.collectionPostcode,
          delivery_miles: wantsDelivery ? milesFor("delivery") : 0,
          collection_miles: wantsCollection ? milesFor("collection") : 0,
          customer: { name: form.name, dob: form.dob, address: form.address, postcode: form.postcode, phone: form.phone, email: form.email },
          ni_number: form.niNumber,
          agreement_accepted: form.agreementAccepted,
        },
      });
      let paymentStatus = "not_configured";
      for (const [kind, file] of [["licence_front", form.licenceFront], ["licence_back", form.licenceBack], ["proof", form.proofDoc]]) {
        if (file) {
          const fd = new FormData();
          fd.append("file", file);
          await apiFetch(`/bookings/${res.ref}/documents?kind=${kind}`, { method: "POST", formData: fd });
        }
      }
      if (res.payment?.enabled) {
        if (!stripe || !elements) throw new Error("Payment form is not ready yet — please wait a moment and try again.");
        const { error: formError } = await elements.submit();
        if (formError) throw new Error(formError.message || "Please complete your payment details.");
        const { error, paymentIntent } = await stripe.confirmPayment({
          elements,
          clientSecret: res.payment.client_secret,
          confirmParams: {
            return_url: `${window.location.origin}/payment-return?type=rental&ref=${res.ref}`,
            payment_method_data: { billing_details: { name: form.name, email: form.email, address: { country: "GB", postal_code: form.postcode } } },
          },
          redirect: "if_required",
        });
        if (error) throw new Error(error.message || "Payment authorisation failed");
        if (paymentIntent?.status === "requires_capture") {
          await apiFetch(`/bookings/${res.ref}/confirm-payment`, { method: "POST", body: { payment_intent_id: paymentIntent.id } });
          paymentStatus = "authorised";
        } else {
          throw new Error("The payment was not authorised — no money has been taken. Please check your payment details and try again, or choose a different payment method.");
        }
      }
      setSubmitted({ enquiry: false, ref: res.ref, total: res.total, vehicleName: vehicle.name, paymentStatus });
    } catch (e) {
      setNotice(e.message || "Submission failed — please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (submitted) return <Confirmation submitted={submitted} customerName={form.name || enquiry.name} />;

  return (
    <div className="bg-[#050506] text-zinc-100 min-h-screen">
      <div className="vignette-overlay" />
      <header className="border-b border-white/[0.08] relative z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="booking-home-link" className="flex items-center gap-3">
            <img src={MARK} alt="Senna Prestige Travels" className="h-8 w-auto" />
            <span className="hidden sm:block text-[0.6rem] tracking-[0.42em] uppercase text-zinc-300">Booking</span>
          </Link>
          <Link to="/" data-testid="booking-back-link" className="nav-link text-[0.62rem] tracking-[0.3em] uppercase text-zinc-400">Back to Site</Link>
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 py-14 md:py-20">
        <p className="kicker mb-4">Your Journey, Upgraded</p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.15] max-w-2xl">Book Your Journey</h1>

        <div className="lg:hidden mt-8 mb-10">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[0.6rem] tracking-[0.3em] uppercase text-zinc-400">Step {step + 1} of {STEPS.length}</span>
            <span className="text-[0.6rem] tracking-[0.3em] uppercase text-white">{STEPS[step]}</span>
          </div>
          <div className="h-px bg-white/10">
            <div className="h-px bg-white transition-all duration-700" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-[290px_1fr] gap-12 lg:gap-16">
          <aside className="hidden lg:block">
            <div className="sticky top-10 divide-y divide-white/[0.06] border-y border-white/[0.06]">
              {STEPS.map((s, i) => (
                <button
                  key={s}
                  data-testid={`bk-stepnav-${i + 1}`}
                  onClick={() => i < step && !(enquiryOnly && i > 1) && setStep(i)}
                  className={`w-full flex items-center gap-5 px-5 py-4 text-left transition-colors duration-300 ${i === step ? "bg-white/[0.05]" : ""} ${i < step ? "cursor-pointer" : "cursor-default"}`}
                >
                  <span className={`font-display text-sm w-6 ${i < step ? "text-emerald-300" : i === step ? "text-white" : "text-zinc-600"}`}>
                    {i < step ? <Check size={14} /> : String(i + 1).padStart(2, "0")}
                  </span>
                  <span className={`text-[0.62rem] tracking-[0.25em] uppercase ${i === step ? "text-white" : "text-zinc-500"}`}>{s}</span>
                </button>
              ))}
            </div>
          </aside>

          <div className="min-h-[420px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={step + (enquiryOnly ? "-enq" : "")}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -14 }}
                transition={{ duration: 0.5, ease: EASE }}
              >
                {step === 0 && (
                  <div>
                    <StepTitle n="01" title="Select Vehicle" sub="Choose from the current Senna Prestige fleet. Full pricing is shown with every vehicle." />
                    <div className="grid grid-cols-1 xl:grid-cols-[1.2fr_1fr] gap-8 items-start">
                      <div className="space-y-3">
                        {VEHICLES.map((v) => (
                          <button key={v.id} data-testid={`bk-vehicle-${v.id}`} onClick={() => set("vehicleId", v.id)} className={`radio-row ${form.vehicleId === v.id ? "selected" : ""}`}>
                            <span className="font-display text-lg text-white flex items-center gap-3">
                              {v.name}
                              {v.enquiryOnly && <span className="text-[0.52rem] tracking-[0.25em] uppercase text-zinc-500 font-sans">Enquire for Price</span>}
                            </span>
                            {!v.enquiryOnly && (
                              <span className="text-sm text-zinc-400 font-light">From {gbp(v.prices.daily)} / day · Deposit {gbp(v.deposit)}</span>
                            )}
                          </button>
                        ))}
                      </div>
                      {vehicle?.photos?.length > 0 && (
                        <div className="xl:sticky xl:top-8">
                          <VehiclePhoto vehicle={vehicle} />
                          <p className="mt-4 text-[0.62rem] tracking-[0.3em] uppercase text-zinc-400">{vehicle.name}</p>
                          {vehicle.enquiryOnly && (
                            <p className="mt-2 text-sm text-zinc-500 font-light">Enquiry only — pricing arranged directly via WhatsApp on {WHATSAPP_DISPLAY}.</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {step === 1 && vehicle && enquiryOnly && (
                  <div>
                    <StepTitle n="02" title={`Enquire — ${vehicle.name}`} sub="This vehicle is priced per enquiry. Send your request and our team will confirm availability and pricing directly." />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
                      <div className="space-y-5">
                        <Field label="Full Name" testid="enq-name" placeholder="Your full name" value={enquiry.name} onChange={(e) => setEnq("name", e.target.value)} />
                        <Field label="Phone Number" testid="enq-phone" placeholder="Best contact number" value={enquiry.phone} onChange={(e) => setEnq("phone", e.target.value)} />
                        <Field label="Email (optional)" testid="enq-email" type="email" placeholder="you@example.com" value={enquiry.email} onChange={(e) => setEnq("email", e.target.value)} />
                        <Field label="Preferred Dates (optional)" testid="enq-dates" placeholder="e.g. 12–15 September" value={enquiry.preferred_dates} onChange={(e) => setEnq("preferred_dates", e.target.value)} />
                        <label className="block">
                          <span className="field-label">Message (optional)</span>
                          <textarea data-testid="enq-message" className="field min-h-[90px] resize-y" placeholder="Anything we should know…" value={enquiry.message} onChange={(e) => setEnq("message", e.target.value)} />
                        </label>
                        <div className="flex flex-col sm:flex-row gap-4">
                          <button data-testid="enq-submit" onClick={submitEnquiry} disabled={busy} className="btn-primary justify-center">
                            {busy ? "Sending…" : "Send Enquiry"} <ArrowRight size={14} />
                          </button>
                          <a data-testid="enq-whatsapp" href={waLink(`Hello Senna Prestige Travels — I would like to enquire about hiring the ${vehicle.name}.`)} target="_blank" rel="noopener noreferrer" className="btn-ghost justify-center">
                            <MessageCircle size={14} /> WhatsApp {WHATSAPP_DISPLAY}
                          </a>
                        </div>
                      </div>
                      <VehiclePhoto vehicle={vehicle} />
                    </div>
                  </div>
                )}

                {step === 1 && vehicle && !enquiryOnly && (
                  <div>
                    <StepTitle n="02" title="Select Hire Package" sub={`Pricing for the ${vehicle.name}.`} />
                    {vehicle.photos?.length > 0 && (
                      <div className="mb-8 max-w-md"><VehiclePhoto vehicle={vehicle} compact /></div>
                    )}
                    <div className="space-y-3">
                      {PACKAGES.map((p) => (
                        <button key={p.id} data-testid={`bk-package-${p.id}`} onClick={() => set("packageId", p.id)} className={`radio-row ${form.packageId === p.id ? "selected" : ""}`}>
                          <span>
                            <span className="text-[0.7rem] tracking-[0.3em] uppercase text-white block">{p.label}</span>
                            {p.note && <span className="text-xs text-zinc-500 font-light block mt-1">{p.note}</span>}
                          </span>
                          <span className="font-display text-xl text-white">{gbp(vehicle.prices[p.id])}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div>
                    <StepTitle n="03" title="Select Dates" sub={isWeekend ? "Weekend hire runs Friday morning to Monday morning — choose a Friday and the return Monday is set automatically." : "Choose your collection and return dates."} />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <Field label={isWeekend ? "Hire Start Date (Friday)" : "Hire Start Date"} testid="bk-field-start-date" type="date" value={form.startDate} onChange={(e) => onStartDate(e.target.value)} />
                      <div>
                        <Field label={isWeekend ? "Hire End Date (Monday — automatic)" : "Hire End Date"} testid="bk-field-end-date" type="date" value={form.endDate} onChange={(e) => !isWeekend && set("endDate", e.target.value)} disabled={isWeekend} />
                      </div>
                    </div>
                    {isWeekend && (
                      <p className="mt-5 text-[0.62rem] tracking-[0.2em] uppercase text-zinc-500">Friday morning → Monday morning</p>
                    )}
                    {nights > 0 && (
                      <p className="mt-4 text-sm text-zinc-400 font-light flex items-center gap-3">
                        <BadgeCheck size={15} className="text-zinc-500" />
                        {fmtDate(form.startDate)} → {fmtDate(form.endDate)} · {nights} {nights === 1 ? "day" : "days"}
                      </p>
                    )}
                  </div>
                )}

                {step === 3 && (
                  <div>
                    <StepTitle n="04" title="Collection / Delivery" sub="Senna Prestige Travels is based in Peterborough and covers nationwide delivery at £2.20 per mile." />
                    <div className="space-y-3">
                      {LOGISTICS_OPTIONS.map((o) => (
                        <button key={o.id} data-testid={`bk-logistics-${o.id}`} onClick={() => set("logistics", o.id)} className={`radio-row ${form.logistics === o.id ? "selected" : ""}`}>
                          <span>
                            <span className="text-[0.7rem] tracking-[0.28em] uppercase text-white block">{o.label}</span>
                            <span className="text-xs text-zinc-500 font-light block mt-1">{o.desc}</span>
                          </span>
                          <MapPin size={15} className={form.logistics === o.id ? "text-white" : "text-zinc-600"} />
                        </button>
                      ))}
                    </div>

                    {(wantsDelivery || wantsCollection) && (
                      <>
                        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                          {wantsDelivery && (
                            <Field label="Delivery Postcode / Location" testid="bk-field-delivery-postcode" placeholder="Where should we deliver?" value={form.deliveryPostcode} onChange={(e) => set("deliveryPostcode", e.target.value)} />
                          )}
                          {wantsCollection && (
                            <Field label="Collection Postcode / Location" testid="bk-field-collection-postcode" placeholder="Where should we collect from?" value={form.collectionPostcode} onChange={(e) => set("collectionPostcode", e.target.value)} />
                          )}
                        </div>
                        <p className="mt-5 text-[0.62rem] tracking-[0.12em] text-zinc-500 font-light leading-relaxed flex items-start gap-2">
                          <MapPin size={13} className="mt-0.5 shrink-0 text-zinc-600" />
                          <span>Your road distance from/to Peterborough is charged at £2.20 per mile — delivery and collection are two separate journeys.{distConfigured ? "" : " Automatic distance calculation will be connected in Phase 2."}</span>
                        </p>

                        {distConfigured && (
                          <div className="mt-6 space-y-2 text-sm font-light" data-testid="bk-live-distance">
                            {wantsDelivery && (
                              <p className="text-zinc-400 flex justify-between gap-4" data-testid="bk-live-delivery">
                                <span>Delivery — PE4 6BX → {form.deliveryPostcode || "your postcode"}</span>
                                <span className="text-white whitespace-nowrap">
                                  {distBusy === "delivery" ? "Calculating…" : liveDistance.delivery ? `${liveDistance.delivery.miles} miles · ${gbp(liveDistance.delivery.charge)}` : "Enter postcode"}
                                </span>
                              </p>
                            )}
                            {wantsCollection && (
                              <p className="text-zinc-400 flex justify-between gap-4" data-testid="bk-live-collection">
                                <span>Collection — {form.collectionPostcode || "your postcode"} → PE4 6BX</span>
                                <span className="text-white whitespace-nowrap">
                                  {distBusy === "collection" ? "Calculating…" : liveDistance.collection ? `${liveDistance.collection.miles} miles · ${gbp(liveDistance.collection.charge)}` : "Enter postcode"}
                                </span>
                              </p>
                            )}
                          </div>
                        )}

                        {distConfigured === false && (
                          <div className="mt-8 p-5 md:p-6 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)] bg-white/[0.015]">
                            <div className="flex items-center justify-between mb-5">
                              <span className="text-[0.58rem] tracking-[0.28em] uppercase text-zinc-500">Demo Distance Control</span>
                              <DemoTag>Demo — Phase 1</DemoTag>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                              {wantsDelivery && (
                                <Field label="Demo Delivery Miles" testid="bk-field-delivery-miles" type="number" min="0" placeholder="e.g. 40" value={form.deliveryMiles} onChange={(e) => set("deliveryMiles", e.target.value)} />
                              )}
                              {wantsCollection && (
                                <Field label="Demo Collection Miles" testid="bk-field-collection-miles" type="number" min="0" placeholder="e.g. 40" value={form.collectionMiles} onChange={(e) => set("collectionMiles", e.target.value)} />
                              )}
                            </div>
                            <div className="mt-5 space-y-2 text-sm font-light">
                              {wantsDelivery && deliveryCharge > 0 && <p className="text-zinc-400 flex justify-between"><span>Delivery — demo miles × £2.20</span><span className="text-white">{gbp(deliveryCharge)}</span></p>}
                              {wantsCollection && collectionCharge > 0 && <p className="text-zinc-400 flex justify-between"><span>Collection — demo miles × £2.20</span><span className="text-white">{gbp(collectionCharge)}</span></p>}
                              {deliveryCharge === 0 && collectionCharge === 0 && (
                                <p className="text-[0.62rem] tracking-[0.12em] text-zinc-600 font-light">Enter demo miles above to preview per-mile pricing. This control disappears once live distance calculation is activated.</p>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {step === 4 && (
                  <div>
                    <StepTitle n="05" title="Customer Details" sub="Your details are used on the rental agreement." />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <Field label="Full Name" testid="bk-field-name" placeholder="Your full name" value={form.name} onChange={(e) => set("name", e.target.value)} />
                      <Field label="Date of Birth" testid="bk-field-dob" type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)} />
                      <Field label="Home Address" testid="bk-field-address" placeholder="Street address" value={form.address} onChange={(e) => set("address", e.target.value)} />
                      <Field label="Postcode" testid="bk-field-postcode" placeholder="Postcode" value={form.postcode} onChange={(e) => set("postcode", e.target.value)} />
                      <Field label="Contact Number" testid="bk-field-phone" placeholder="Phone number" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                      <Field label="Email Address" testid="bk-field-email" type="email" placeholder="you@example.com" value={form.email} onChange={(e) => set("email", e.target.value)} />
                    </div>
                  </div>
                )}

                {step === 5 && (
                  <div>
                    <StepTitle n="06" title="Driver Verification" sub="Minimum driver age: 21. The following documents are required before a booking can be approved." />
                    <div className="space-y-4">
                      <FilePick label="Driving Licence — Front" file={form.licenceFront} onPick={(v) => set("licenceFront", v)} testid="bk-file-licence-front" hint="Clear photo or scan of the front of your driving licence (jpg, png, webp or pdf, max 8MB)." />
                      <FilePick label="Driving Licence — Back" file={form.licenceBack} onPick={(v) => set("licenceBack", v)} testid="bk-file-licence-back" hint="Clear photo or scan of the back of your driving licence." />
                      <FilePick label="Bank Statement or Utility Bill" file={form.proofDoc} onPick={(v) => set("proofDoc", v)} testid="bk-file-proof" hint="No older than 6 months." />
                      <Field label="National Insurance Number (Optional)" testid="bk-field-ni" placeholder="Optional" value={form.niNumber} onChange={(e) => set("niNumber", e.target.value)} />
                    </div>
                    <p className="mt-6 text-[0.62rem] tracking-[0.12em] text-zinc-600 font-light flex items-center gap-2">
                      <ShieldCheck size={13} /> Documents are uploaded securely and are only visible to the Senna Prestige team — never public.
                    </p>
                  </div>
                )}

                {step === 6 && (
                  <div>
                    <StepTitle n="07" title="Rental Agreement" sub="Review the Vehicle Rental Agreement & Terms & Conditions before checkout." />
                    <div className="p-6 md:p-8 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)] bg-white/[0.02] flex flex-col sm:flex-row sm:items-center gap-6 justify-between">
                      <div className="flex items-start gap-4">
                        <BookOpen size={20} strokeWidth={1.5} className="text-zinc-400 mt-1" />
                        <div>
                          <p className="text-[0.7rem] tracking-[0.28em] uppercase text-white">Vehicle Rental Agreement & Terms & Conditions</p>
                          <p className="mt-2 text-sm text-zinc-400 font-light leading-relaxed">Includes hire terms, renter responsibilities, the condition checklist and the repair cost schedule.</p>
                        </div>
                      </div>
                      <button data-testid="bk-open-agreement" className="btn-ghost !py-3 !px-6 shrink-0" onClick={() => setAgreementOpen(true)}>Read Agreement</button>
                    </div>
                    <button
                      data-testid="bk-agree-checkbox"
                      onClick={() => set("agreementAccepted", !form.agreementAccepted)}
                      className="mt-6 flex items-start gap-4 text-left group"
                    >
                      <span className={`chk mt-0.5 ${form.agreementAccepted ? "on" : ""}`}>{form.agreementAccepted && <Check size={12} />}</span>
                      <span className="text-sm text-zinc-300 font-light leading-relaxed">
                        I confirm that I have read and agree to the Vehicle Rental Agreement and Terms & Conditions.
                      </span>
                    </button>
                    {form.agreementAccepted && (
                      <p className="mt-4 text-[0.62rem] tracking-[0.15em] text-emerald-300/80 font-light flex items-center gap-2">
                        <Check size={13} /> Your acceptance is recorded against this booking with a timestamp.
                      </p>
                    )}
                  </div>
                )}

                {step === 7 && vehicle && pkg && (
                  <div>
                    <StepTitle n="08" title="Payment Summary" sub="Review your hire breakdown before submitting for approval." />
                    <div className="shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)] bg-white/[0.02] p-6 md:p-8">
                      <SummaryRow label="Vehicle" value={vehicle.name} />
                      <SummaryRow label="Hire Package" value={`${pkg.label} — ${fmtDate(form.startDate)} → ${fmtDate(form.endDate)}`} />
                      <SummaryRow label="Hire Price" value={gbp(hirePrice)} />
                      {wantsDelivery && (
                        <SummaryRow
                          label="Delivery Charge"
                          value={deliveryCharge > 0 ? `${gbp(deliveryCharge)}${liveDistance.delivery ? ` (${liveDistance.delivery.miles} miles)` : distConfigured === false ? " (demo distance)" : ""}` : "Confirmed by distance calculation"}
                        />
                      )}
                      {wantsCollection && (
                        <SummaryRow
                          label="Collection Charge"
                          value={collectionCharge > 0 ? `${gbp(collectionCharge)}${liveDistance.collection ? ` (${liveDistance.collection.miles} miles)` : distConfigured === false ? " (demo distance)" : ""}` : "Confirmed by distance calculation"}
                        />
                      )}
                      <SummaryRow label="Security Deposit (refundable)" value={gbp(deposit)} />
                      <div className="flex items-center justify-between pt-5 mt-2 border-t border-white/[0.14]">
                        <span className="text-[0.68rem] tracking-[0.3em] uppercase text-white">Total</span>
                        <span className="font-display text-2xl text-white" data-testid="bk-total">{gbp(total)}</span>
                      </div>
                    </div>

                    <p className="mt-8 text-[0.62rem] tracking-[0.12em] text-zinc-500 font-light flex items-center gap-2">
                      <Lock size={12} /> {payConfig?.enabled
                        ? "Secure payment details are taken on the final step — authorisation only, captured when your booking is approved."
                        : "Online card authorisation is being activated for this website. Your booking is submitted securely and our team will confirm payment arrangements with you directly."}
                    </p>
                  </div>
                )}

                {step === 8 && vehicle && pkg && (
                  <div>
                    <StepTitle n="09" title="Submit for Approval" sub="One final review before your booking is sent to our team." />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/[0.08] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]">
                      {[
                        ["Vehicle", vehicle.name],
                        ["Package", pkg.label],
                        ["Dates", `${fmtDate(form.startDate)} → ${fmtDate(form.endDate)}`],
                        ["Logistics", LOGISTICS_OPTIONS.find((l) => l.id === form.logistics).label],
                        ["Renter", form.name],
                        ["Documents", "Licence front, licence back, proof of address"],
                        ["Agreement", "Accepted"],
                        ["Total", gbp(total)],
                      ].map(([l, v]) => (
                        <div key={l} className="bg-[#050506] px-6 py-5">
                          <p className="text-[0.55rem] tracking-[0.3em] uppercase text-zinc-500 mb-2">{l}</p>
                          <p className="text-sm text-zinc-200 font-light">{v}</p>
                        </div>
                      ))}
                    </div>
                    {payConfig?.enabled && (
                      <div className="mt-8">
                        <span className="field-label">Payment — Secure Authorisation</span>
                        <div className="stripe-field" data-testid="bk-payment-element">
                          <PaymentElement
                            options={{ layout: "tabs" }}
                            onReady={() => setPayMounted(true)}
                            onChange={(e) => { setCardReady(e.complete); setPayMethod(e.value?.type || "card"); }}
                          />
                        </div>
                        <p className="mt-3 text-[0.62rem] tracking-[0.12em] text-zinc-500 font-light flex items-center gap-2">
                          <Lock size={12} /> {payMethod === "card"
                            ? `Your card is authorised for ${gbp(total)} — nothing is captured until your booking is approved. If declined, the authorisation is cancelled and your bank releases the hold in line with their own timescales.`
                            : `${gbp(total)} is authorised via your chosen payment method — nothing is captured until your booking is approved. If declined, the authorisation is cancelled and your provider releases the hold in line with their own timescales.`}
                        </p>
                      </div>
                    )}
                    <button
                      data-testid="bk-submit"
                      onClick={submit}
                      disabled={busy || (payConfig?.enabled && (!cardReady || !payMounted))}
                      className="btn-primary mt-8 w-full sm:w-auto justify-center disabled:opacity-50 disabled:pointer-events-none"
                    >
                      {busy ? "Submitting Securely…" : "Submit Booking for Approval"} <ArrowRight size={14} />
                    </button>
                    {payConfig?.enabled && (!cardReady || !payMounted) && (
                      <p className="mt-3 text-[0.62rem] tracking-[0.12em] text-zinc-500 font-light">Complete the secure payment details above to enable submission.</p>
                    )}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {notice && (
              <p data-testid="bk-notice" className="mt-8 w-full text-sm text-amber-200/90 font-light leading-relaxed">{notice}</p>
            )}
            <div className="mt-6 pt-8 border-t border-white/[0.08] flex items-center justify-between">
              <button
                data-testid="bk-back"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                className={`btn-ghost !py-3 !px-6 ${step === 0 ? "opacity-0 pointer-events-none" : ""}`}
              >
                <ArrowLeft size={13} /> Back
              </button>
              <div className="flex items-center gap-5">
                {step < STEPS.length - 1 && !(enquiryOnly && step === 1) && (
                  <button data-testid="bk-continue" onClick={next} disabled={busy} className="btn-primary !py-3 !px-7 disabled:opacity-50">
                    {busy ? "Checking…" : "Continue"} <ArrowRight size={13} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <AgreementModal open={agreementOpen} onClose={() => setAgreementOpen(false)} />
    </div>
  );
}

const Confirmation = ({ submitted, customerName }) => (
  <div className="bg-[#050506] text-zinc-100 min-h-screen flex flex-col">
    <div className="vignette-overlay" />
    <header className="border-b border-white/[0.08] relative z-10">
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center">
        <Link to="/" data-testid="confirm-home-link" className="flex items-center gap-3">
          <img src={MARK} alt="Senna Prestige Travels" className="h-8 w-auto" />
        </Link>
      </div>
    </header>
    <main className="relative z-10 flex-1 flex items-center justify-center px-6 py-20">
      <motion.div
        data-testid="bk-confirmation"
        className="max-w-2xl w-full text-center"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: EASE }}
      >
        <span className="inline-flex items-center justify-center w-16 h-16 rounded-full shadow-[inset_0_0_0_1px_rgba(255,255,255,0.25)] mb-8">
          <Check size={26} className="text-white" strokeWidth={1.5} />
        </span>
        {submitted.enquiry ? (
          <>
            <p className="kicker mb-4">Vehicle Enquiry</p>
            <h1 className="font-display text-4xl sm:text-5xl text-white">Enquiry Sent</h1>
            <p className="mt-8 text-sm md:text-base text-zinc-400 font-light leading-relaxed max-w-lg mx-auto">
              Thank you{customerName ? `, ${customerName.split(" ")[0]}` : ""}. Your enquiry for the {submitted.vehicleName} has been sent to our team — we will confirm availability and pricing directly. For a faster response, message us on WhatsApp: {WHATSAPP_DISPLAY}.
            </p>
          </>
        ) : (
          <>
            <p className="kicker mb-4">Booking Reference {submitted.ref}</p>
            <h1 className="font-display text-4xl sm:text-5xl text-white">Booking Submitted</h1>
            <span className="status-pill text-amber-200 border border-amber-200/30 inline-block mt-6">Pending Approval</span>
            <p className="mt-8 text-sm md:text-base text-zinc-400 font-light leading-relaxed max-w-lg mx-auto">
              Thank you, {customerName.split(" ")[0]}. Your booking for the {submitted.vehicleName} has been submitted and will be reviewed by our team before final confirmation.
              {submitted.paymentStatus === "authorised"
                ? ` Your payment of ${gbp(submitted.total)} is authorised only — captured once approved, or released in full if declined.`
                : " Our team will confirm payment arrangements with you directly."}
            </p>
            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/[0.08] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)] text-left">
              {["Submitted", "Under Review", "Approved", "Payment Captured"].map((s, i) => (
                <div key={s} className="bg-[#050506] px-5 py-5">
                  <span className={`font-display text-sm ${i === 0 ? "text-emerald-300" : "text-zinc-600"}`}>{String(i + 1).padStart(2, "0")}</span>
                  <p className={`mt-3 text-[0.6rem] tracking-[0.25em] uppercase ${i === 0 ? "text-white" : "text-zinc-500"}`}>{s}</p>
                </div>
              ))}
            </div>
          </>
        )}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-5">
          <Link to="/" data-testid="confirm-home-button" className="btn-ghost justify-center">Return to Home</Link>
        </div>
      </motion.div>
    </main>
  </div>
);
